import type { ChargeQuestionCountsDto, TelemetryOverviewDto, TelemetryRunDetailDto, TelemetryRunListParams, TelemetryRunPageDto } from '@signalvote/shared';
import { campaignHeaders, defineQuery } from '@/lib/data';
import { apiJson } from './apiFetch';
import { buildQueryString } from './queryUtils';
import type { TelemetryOverviewParams } from '../../lib/queryKeys';

const BASE = '/api/admin/assistant-telemetry';

/**
 * Platform-admin only (the server 403s anyone else) and subsidiary-wide: the `campaignId` param
 * filters, the selected campaign at the head of the key does not.
 */
export const useAssistantTelemetryOverview = defineQuery({
  key: (params: TelemetryOverviewParams) => ['assistantTelemetry', 'overview', params],
  reads: 'GET /api/admin/assistant-telemetry/overview',
  keepPrevious: true,
  fetch: (params, { signal, campaignId }) =>
    apiJson<TelemetryOverviewDto>(`${BASE}/overview${buildQueryString(params)}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch assistant telemetry',
    }),
});

export const useAssistantTelemetryRuns = defineQuery({
  key: (params: TelemetryRunListParams) => ['assistantTelemetry', 'runs', params],
  reads: 'GET /api/admin/assistant-telemetry/runs',
  keepPrevious: true,
  fetch: (params, { signal, campaignId }) =>
    apiJson<TelemetryRunPageDto>(`${BASE}/runs${buildQueryString({ ...params, page: params.page?.toString() })}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch assistant runs',
    }),
});

export const useAssistantTelemetryRun = defineQuery({
  key: (runId: string) => ['assistantTelemetry', 'run', runId],
  reads: 'GET /api/admin/assistant-telemetry/runs/{id}',
  fetch: (runId, { signal, campaignId }) =>
    apiJson<TelemetryRunDetailDto>(`${BASE}/runs/${encodeURIComponent(runId)}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch the assistant run',
    }),
});

export const useAssistantChargeQuestions = defineQuery({
  key: (params: TelemetryOverviewParams) => ['assistantTelemetry', 'chargeQuestions', params],
  reads: 'GET /api/admin/assistant-telemetry/charge-questions',
  keepPrevious: true,
  fetch: (params, { signal, campaignId }) =>
    apiJson<ChargeQuestionCountsDto>(`${BASE}/charge-questions${buildQueryString(params)}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch charge question counts',
    }),
});
